import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, Loader2, CheckCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { reportWebsiteFormLead } from "@/lib/googleAdsConversion";

interface GetLowestPriceProps {
  title: string;
  stockNumber: string;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  /** Render as full-width prominent button (detail page) vs compact (card overlay) */
  variant?: "prominent" | "compact";
  className?: string;
}

const CRM_ENDPOINT = `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/website-lead`;

export default function GetLowestPrice({
  title,
  stockNumber,
  year,
  make,
  model,
  variant = "prominent",
  className = "",
}: GetLowestPriceProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const fd = new FormData(formRef.current!);
    const firstName = (fd.get("first_name") as string)?.trim() || "";
    const lastName = (fd.get("last_name") as string)?.trim() || "";
    const email = (fd.get("email") as string)?.trim() || "";
    const phone = (fd.get("phone") as string)?.trim() || "";

    if (!firstName) {
      toast({ title: "First name is required", variant: "destructive" });
      setSubmitting(false);
      return;
    }
    if (!lastName) {
      toast({ title: "Last name is required", variant: "destructive" });
      setSubmitting(false);
      return;
    }
    if (!email && !phone) {
      toast({ title: "Please provide email or phone", variant: "destructive" });
      setSubmitting(false);
      return;
    }

    try {
      // Get UTM params
      const params = new URLSearchParams(window.location.search);
      const utm = {
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
      };

      const sessionId = (() => { try { return sessionStorage.getItem("_pv_sid") || ""; } catch { return ""; } })();

      const formData: Record<string, unknown> = {
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        phone_mobile: phone,
        source_display: `Get Lowest Price — ${title}`,
        stock_number: stockNumber,
        vehicle_title: title,
        vehicle_year: year?.toString() || "",
        vehicle_make: make || "",
        vehicle_model: model || "",
        comments: `Customer requested the lowest price on ${title} (Stock #${stockNumber}).`,
        page_url: window.location.href,
        referrer: document.referrer,
        session_id: sessionId,
        ...utm,
      };

      // Submit to CRM endpoint (handles both local save and CRM forwarding)
      const res = await fetch(CRM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!data.success) {
        // Fallback: try direct DB insert
        const customerName = [firstName, lastName].filter(Boolean).join(" ") || null;
        const { error } = await supabase.from("form_submissions").insert({
          form_type: "Get Lowest Price",
          customer_name: customerName,
          customer_email: email || null,
          customer_phone: phone || null,
          form_data: formData as any,
        });
        if (error) throw error;
      }

      setSuccess(true);
      void reportWebsiteFormLead();
      toast({ title: "Request sent!", description: "Our team will get you the lowest price." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to submit", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (success && !open) setSuccess(false); // Reset on reopen

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSuccess(false); }}>
      <DialogTrigger asChild>
        {variant === "prominent" ? (
          <Button size="lg" className={`w-full font-heading uppercase gap-2 bg-primary hover:bg-primary/90 ${className}`} aria-label={`Unlock best price on ${title}`}>
            <DollarSign className="h-5 w-5" /> Get Out The Door Price
          </Button>
        ) : (
          <Button size="sm" variant="default" className={`font-heading uppercase gap-1.5 text-xs ${className}`} aria-label={`Unlock best price on ${title}`}>
            <DollarSign className="h-3.5 w-3.5" /> Get Out The Door Price
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" /> Get Out The Door Price
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          RE: <span className="font-medium text-foreground">{title}</span> (Stock #{stockNumber})
        </p>

        {success ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <CheckCircle className="h-12 w-12 text-primary" />
            <h3 className="font-heading text-lg font-bold text-foreground">We're On It!</h3>
            <p className="text-muted-foreground text-sm">Our team will reach out shortly with the lowest price for this unit.</p>
          </div>
        ) : (
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input name="first_name" required maxLength={100} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input name="last_name" required maxLength={100} className="h-9" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input name="email" type="email" maxLength={255} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input name="phone" type="tel" maxLength={20} className="h-9" />
            </div>
            <Button type="submit" size="lg" className="w-full font-heading uppercase gap-2" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4" />}
              {submitting ? "Sending…" : "Get Out The Door Price"}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">No obligation. Our team will respond ASAP.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
